
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Factory } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatMetauxHauteGaronne = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Métaux Haute-Garonne (31) - Rachat Fer Cuivre Toulouse | Groupe Fabre"
        description="Rachat de métaux en Haute-Garonne : fer, cuivre, aluminium, inox. Service rapide Toulouse, Muret, Colomiers. Pesée certifiée et paiement immédiat."
        canonical="/nos-services/achat-metaux/haute-garonne"
        keywords="achat métaux Haute-Garonne, rachat cuivre Toulouse, ferraille 31, métaux Muret"
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-fabre-green to-fabre-darkgreen text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Factory className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat Métaux Haute-Garonne</h1>
                  <p className="text-xl mt-2 opacity-90">Rachat de métaux ferreux et non ferreux dans le 31</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de métaux en Haute-Garonne (31)
                </h2>
                <p className="text-gray-600 mb-6">
                  <strong>Groupe Fabre Environnement</strong> propose ses services d'achat de métaux 
                  dans toute la <strong>Haute-Garonne</strong>. Nous intervenons à Toulouse, 
                  Muret, Colomiers et dans toutes les communes du département 31.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Zones d'intervention 31</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <MapPin className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                    <span><strong>Toulouse :</strong> métropole et agglomération</span>
                  </li>
                  <li className="flex items-start">
                    <MapPin className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                    <span><strong>Muret :</strong> zone industrielle et particuliers</span>
                  </li>
                  <li className="flex items-start">
                    <MapPin className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                    <span><strong>Colomiers :</strong> secteur résidentiel et entreprises</span>
                  </li>
                  <li className="flex items-start">
                    <MapPin className="h-5 w-5 text-fabre-green mr-3 mt-1" />
                    <span><strong>Toutes communes :</strong> déplacement possible</span>
                  </li>
                </ul>

                <div className="bg-fabre-lightgreen border border-fabre-green p-6 rounded-lg">
                  <h4 className="font-bold text-fabre-darkgreen mb-3">Métaux rachetés en Haute-Garonne</h4>
                  <ul className="text-fabre-darkgreen text-sm space-y-2">
                    <li>• Cuivre : câbles, tuyaux, fils électriques</li>
                    <li>• Ferraille : fer, acier, fonte</li>
                    <li>• Aluminium : profilés, radiateurs, jantes</li>
                    <li>• Inox : pièces industrielles, équipements</li>
                    <li>• Plomb : batteries, contrepoids</li>
                    <li>• Laiton : robinetterie, instruments</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Service Haute-Garonne</h3>
                <p className="text-gray-600 mb-6">
                  Notre expertise en Haute-Garonne nous permet de vous proposer 
                  les meilleurs prix et un service adapté aux spécificités locales.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Truck className="h-5 w-5 text-fabre-green mr-3" />
                    <span>Déplacement Haute-Garonne</span>
                  </div>
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-fabre-green mr-3" />
                    <span>Prix selon cours métaux</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-fabre-green mr-3" />
                    <span>Paiement immédiat</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-fabre-green mr-3" />
                    <span>Conseil personnalisé</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-fabre-green text-white rounded-md font-medium hover:bg-fabre-darkgreen transition-colors">
                    Demander un devis Haute-Garonne
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Villes principales */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Nos interventions en Haute-Garonne
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-fabre-darkgreen mb-3">Toulouse Métropole</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Centre-ville, périphérie, zones industrielles
                </p>
                <p className="text-fabre-green font-medium">Service premium</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-fabre-darkgreen mb-3">Secteur Sud</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Muret, Portet-sur-Garonne, Roques
                </p>
                <p className="text-fabre-green font-medium">Intervention rapide</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-fabre-darkgreen mb-3">Secteur Ouest</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Colomiers, Plaisance-du-Touch, Tournefeuille
                </p>
                <p className="text-fabre-green font-medium">Service local</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatMetauxHauteGaronne;
